import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Space, notification, theme, Switch } from "antd";
import { NumberOutlined, DeploymentUnitOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import QuantityInputMethod from "../product-management/QuantityInputMethod";
import { QuantityInputMethod as QuantityInputMethodEnum } from "../../../models/order-product/types";
import { IProduct } from "../../../models/product/response";

export interface QuantityData {
    method: QuantityInputMethodEnum;
    count?: number;
    unitPartitionId?: number;
    availableWeight?: number;
}

interface Props {
    visible: boolean;
    product: IProduct | null;
    storeId?: number;
    mode: "add" | "edit";
    initialQuantity?: QuantityData;
    onCancel: () => void;
    onSubmit: (productId: number, quantityData: QuantityData) => void;
}

const ProductQuantityModal: React.FC<Props> = ({
    visible,
    product,
    storeId,
    mode,
    initialQuantity,
    onCancel,
    onSubmit,
}) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const [form] = Form.useForm();
    const [quantityData, setQuantityData] = useState<QuantityData>({
        method: QuantityInputMethodEnum.BY_COUNT,
        count: 1,
    });

    // Reset form when modal opens/closes or product changes
    useEffect(() => {
        if (visible && product) {
            initializeForm();
        }
    }, [visible, product, initialQuantity]);

    const initializeForm = () => {
        if (mode === "edit" && initialQuantity) {
            // Edit mode: load existing quantity data
            setQuantityData(initialQuantity);
        } else {
            // Add mode: start fresh
            setQuantityData({
                method: QuantityInputMethodEnum.BY_COUNT,
                count: 1,
            });
        }
    };

    const getStoreQuantityInfo = () => {
        if (!product?.storeQuantities || !storeId) {
            return null;
        }

        const storeQty = product.storeQuantities.find(
            (sq) => sq.store_id === storeId
        );

        if (!storeQty) {
            return null;
        }

        const factor = product.unit?.factor_accounting_system || 1;
        const availableUnits = storeQty.quantity / factor;

        return {
            available: storeQty.available,
            quantity: availableUnits,
            storeName: storeQty.store_name,
        };
    };

    const validateForm = (): boolean => {
        if (!product) return false;

        const { method, count, availableWeight } = quantityData;

        // Validate count method
        if (method === QuantityInputMethodEnum.BY_COUNT) {
            if (!count || count < 1) {
                notification.error({
                    message: t("validation_error"),
                    description: t("count_must_be_at_least_1"),
                });
                return false;
            }
        }

        // Validate weight method
        if (method === QuantityInputMethodEnum.BY_WEIGHT) {
            if (
                availableWeight === undefined ||
                availableWeight === null ||
                availableWeight <= 0
            ) {
                notification.error({
                    message: t("validation_error"),
                    description: t("weight_must_be_greater_than_zero"),
                });
                return false;
            }
        }

        return true;
    };

    const handleMethodChange = (checked: boolean) => {
        const newMethod = checked ? QuantityInputMethodEnum.BY_WEIGHT : QuantityInputMethodEnum.BY_COUNT;
        setQuantityData({
            method: newMethod,
            count: newMethod === QuantityInputMethodEnum.BY_COUNT ? 1 : undefined,
            unitPartitionId: newMethod === QuantityInputMethodEnum.BY_COUNT ? quantityData.unitPartitionId : undefined,
            availableWeight: newMethod === QuantityInputMethodEnum.BY_WEIGHT ? quantityData.availableWeight : undefined,
        });
    };

    const handleSubmit = () => {
        if (!validateForm() || !product) return;

        onSubmit(product.id, quantityData);
    };

    if (!product) return null;

    const hasUnitPartitions = product.unitPartition && product.unitPartition.length > 0;
    const storeQtyInfo = getStoreQuantityInfo();

    return (
        <Modal
            title={mode === "add" ? t("add_product_quantity") : t("edit_product_quantity")}
            open={visible}
            onCancel={onCancel}
            width={600}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    {t("cancel")}
                </Button>,
                <Button key="submit" type="primary" onClick={handleSubmit}>
                    {mode === "add" ? t("add") : t("save_changes")}
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical">
                <Space direction="vertical" style={{ width: "100%" }} size="large">
                    {/* Product Info */}
                    <div
                        style={{
                            padding: "16px",
                            backgroundColor: token.colorBgContainer,
                            borderRadius: token.borderRadiusLG,
                            border: `1px solid ${token.colorBorder}`,
                        }}
                    >
                        <h4 style={{
                            margin: "0 0 12px 0",
                            color: token.colorText,
                            fontSize: 16,
                            fontWeight: 600,
                        }}>
                            {product.name}
                        </h4>

                        {product.unit && (
                            <div style={{
                                fontSize: 14,
                                color: token.colorTextSecondary,
                                marginBottom: 8,
                            }}>
                                {t("unit")}: {product.unit.name}
                            </div>
                        )}

                        {storeQtyInfo && (
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "8px 12px",
                                backgroundColor: storeQtyInfo.available ? token.colorSuccessBg : token.colorErrorBg,
                                borderRadius: token.borderRadius,
                                border: `1px solid ${storeQtyInfo.available ? token.colorSuccessBorder : token.colorErrorBorder}`,
                                marginTop: 12,
                            }}>
                                <span style={{ fontWeight: 500, color: token.colorTextSecondary }}>
                                    {t("store_available_quantity")}
                                </span>
                                <span style={{ fontSize: 15, fontWeight: 600, color: storeQtyInfo.available ? token.colorSuccess : token.colorError }}>
                                    {storeQtyInfo.available
                                        ? `${storeQtyInfo.quantity} ${product.unit?.name || ""}`
                                        : t("unavailable")}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Quantity Input */}
                    <div
                        style={{
                            padding: "20px",
                            backgroundColor: token.colorBgContainer,
                            borderRadius: token.borderRadiusLG,
                            border: `2px solid ${token.colorPrimary}`,
                        }}
                    >
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: 16,
                            paddingBottom: 12,
                            borderBottom: `2px solid ${token.colorPrimary}`
                        }}>
                            <h4 style={{
                                margin: 0,
                                color: token.colorPrimary,
                                fontSize: 16,
                                fontWeight: 600,
                            }}>
                                {t("quantity")}
                            </h4>
                            {hasUnitPartitions && (
                                <div style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-end",
                                    gap: 6
                                }}>
                                    <Switch
                                        checked={quantityData.method === QuantityInputMethodEnum.BY_WEIGHT}
                                        onChange={handleMethodChange}
                                        checkedChildren={<DeploymentUnitOutlined />}
                                        unCheckedChildren={<NumberOutlined />}
                                    />
                                    <span style={{
                                        fontSize: 11,
                                        color: token.colorTextSecondary,
                                        textAlign: "right",
                                        maxWidth: 200,
                                        lineHeight: 1.4
                                    }}>
                                        {t("quantity_method_helper")}
                                    </span>
                                </div>
                            )}
                        </div>

                        <Form.Item style={{ marginBottom: 0 }}>
                            <QuantityInputMethod
                                value={quantityData}
                                onChange={setQuantityData}
                                product={product}
                            />
                        </Form.Item>
                    </div>
                </Space>
            </Form>
        </Modal>
    );
};

export default ProductQuantityModal;
